import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import CpPanel from 'ember-collapsible-panel/components/cp-panel/component';
import { type IntlService, t } from 'ember-intl';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq, or } from 'ember-truth-helpers';

import Button from 'umg-ember-backend/components/ui/button';
import Form from 'umg-ember-backend/components/ui/form';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { labelValueOptions } from 'umg-ember-backend/utils/intl';

import styles from './styles.module.css';

const bem = getBem(styles);

interface FormFieldSignature {
  Args: {
    field: TODO;
    parentModel: TODO;
    showErrors: boolean;
    deleteRelation: (
      field: TODO,
      model: TODO,
      relationKind: string,
      relationalField: string,
    ) => void;
  };
  Element: HTMLDivElement;
}

export default class FormFieldComponent extends Component<FormFieldSignature> {
  // Services
  @service declare intl: IntlService;

  // Defaults
  fileAccepts = ['image/*', 'video/*', 'audio/*'];
  selectPresets = ['countries'];

  get isDisplayOnly() {
    return ['divider', 'infotext'].includes(this.args.field.type);
  }

  // Getter and setter
  get fileAcceptOptions() {
    return labelValueOptions.call(this, this.fileAccepts, [
      'forms.contest-form-field.options.options.accept',
    ]);
  }

  get selectPresetOptions() {
    return labelValueOptions.call(this, this.selectPresets, [
      'forms.contest-form-field.options.options.preset',
    ]);
  }

  // Functions
  getFileAcceptSelected = (valueString: string) => {
    const values = valueString?.split(',') ?? [];

    return values.map((value) => {
      return this.fileAcceptOptions.find((option) => option.value === value);
    });
  };

  selectFileAccept = (file: TODO, options: { value: string }[]) => {
    const valueString = options.map((option) => option.value).join(',');

    file.settings.accept = valueString;
  };

  // Template
  <template>
    <div
      data-test-contest-form-field
      class={{(bem)}}
      {{sortableItem groupName='contest-form-fields' model=@field}}
      ...attributes
    >
      <div class={{bem 'content'}}>
        <div class={{bem 'handle'}} {{sortableHandle}}>
          {{svgJar 'drag' class=(bem 'drag-indicator')}}
        </div>
        <div class={{bem 'form'}}>
          <CpPanel @open={{@field.isNew}} as |panel|>
            <panel.toggle
              class={{bem 'toggle'}}
              data-test-contest-form-field-toggle
            >
              {{#if (eq @field.type 'infotext')}}
                {{t 'forms.contest-form-field.options.type.infotext'}}
              {{else}}
                {{@field.label}}
                ({{t
                  (concat 'forms.contest-form-field.options.type.' @field.type)
                }})
              {{/if}}
            </panel.toggle>
            <panel.body class={{bem 'body'}}>
              <Form
                @model={{@field}}
                @parentModel={{@parentModel}}
                @showErrors={{@showErrors}}
                as |form|
              >
                <div class='o-layout'>
                  <div class='o-layout__item u-1/1 u-4/6@tablet'>
                    {{#if (eq @field.type 'infotext')}}
                      <form.field
                        @label={{t
                          'record_detail.forms.contest.form_fields.field.infotext'
                        }}
                        @property='label'
                        @type='text-editor'
                        @textEditorPreset='full'
                        @disabled={{@field.isDisabled}}
                        data-test-contest-form-field-label
                      />
                    {{else}}
                      <form.field
                        @property='label'
                        @disabled={{@field.isDisabled}}
                        data-test-contest-form-field-label
                      />
                    {{/if}}
                  </div>
                  <div class='o-layout__item u-1/1 u-1/6@tablet'>
                    {{#unless this.isDisplayOnly}}
                      <form.field
                        @property='required'
                        data-test-contest-form-field-required
                        @type='checkbox'
                        @disabled={{or @field.isDisabled}}
                      />
                    {{/unless}}
                  </div>
                  <div class='o-layout__item u-1/1 u-1/6@tablet'>
                    {{#if
                      (or
                        (eq form.model.type 'text')
                        (eq form.model.type 'textarea')
                      )
                    }}
                      <form.field
                        @property='settings.maxLength'
                        @type='number'
                        min={{0}}
                      />
                    {{/if}}
                  </div>
                </div>

                {{#if (eq form.model.type 'select')}}
                  <form.field
                    @property='settings.preset'
                    @type='select-value'
                    @options={{this.selectPresetOptions}}
                    @disabled={{@field.isDisabled}}
                  />

                  <form.field
                    @property='settings.options'
                    @type='string-array'
                    @disabled={{or
                      @field.isDisabled
                      form.model.settings.preset
                    }}
                  />
                {{/if}}

                {{#if (eq form.model.type 'file')}}
                  <form.field
                    @property='settings.accept'
                    @type='select-multiple'
                    @onChange={{fn this.selectFileAccept form.model}}
                    @options={{this.fileAcceptOptions}}
                    @selected={{this.getFileAcceptSelected
                      form.model.settings.accept
                    }}
                    @disabled={{@field.isDisabled}}
                  />
                {{/if}}
              </Form>
            </panel.body>
          </CpPanel>
        </div>
      </div>
      <div class={{bem 'actions'}}>
        <Button
          @style='outline'
          @icon='delete'
          @iconClass='u-color-error'
          @tooltip={{if
            (eq @field.type 'email')
            (t
              'record_detail.forms.contest.form_fields.field.delete_email_hint'
            )
          }}
          disabled={{or @field.isDisabled (eq @field.type 'email')}}
          data-select-ui-button
          data-test-contest-form-field-delete
          class={{bem 'remove-field'}}
          {{on
            'click'
            (fn @deleteRelation @field @parentModel 'has-many' 'formFields')
          }}
        />
      </div>
    </div>
  </template>
}
